/**
 * // TODO check and adapt for edeka
 * Braintree Hosted Fields Wrapper
 * Handles secure card input fields for credit card payments
 */

import braintree from 'braintree-web';
import { paymentConfig } from '@config/Payment';

/**
 * Hosted Fields instance cache
 */
let hostedFieldsInstance = null;

/**
 * Build Braintree hosted fields configuration based on fieldsHint from backend
 * Only includes fields that are in the hint array
 *
 * @param {Array} fieldsHint - Array of field names to include (e.g., ['number', 'cvv', 'expirationDate', 'postalCode'])
 * @returns {Object} Fields configuration for Braintree
 */
function buildFieldsConfig(fieldsHint = []) {
  const allFields = paymentConfig.braintree.hostedFields.fields;
  const fieldConfig = {};

  // Map fieldsHint names to Braintree field names
  const fieldMapping = {
    number: 'number',
    cvv: 'cvv',
    expiryDate: 'expirationDate',
    expirationDate: 'expirationDate',
    postalCode: 'postalCode'
  };

  // If no hint provided, include all fields (backward compatibility)
  if (!fieldsHint || fieldsHint.length === 0) {
    return allFields;
  }

  // Build config for each field in the hint
  Object.entries(fieldMapping).forEach(([hintName, braintreeName]) => {
    if (fieldsHint.includes(hintName) && allFields[braintreeName]) {
      fieldConfig[braintreeName] = allFields[braintreeName];
    }
  });

  return fieldConfig;
}

/**
 * Create Braintree Hosted Fields instance
 * Mounted to DOM elements for secure card input
 *
 * @param {Object} braintreeClient - Braintree client instance
 * @param {Object} [options] - Optional configuration overrides
 * @param {Array} [options.fieldsHint] - Array of field names from backend to create
 * @returns {Promise<Object>} Hosted Fields instance
 */
export async function createHostedFields(braintreeClient, options = {}) {
  try {
    // console.log('🔵 Creating Braintree Hosted Fields...');

    // Prep fields with translations if 't' function is provided
    const { fields: customFields, styles: customStyles, t } = options;

    // Build fields config dynamically based on backend hint, but allow override via `fields` option
    const builtFields = buildFieldsConfig(options.fieldsHint);
    const fieldsConfig = customFields || builtFields;

    // Resolve placeholders and aria-labels if translation function exists
    if (t) {
      Object.keys(fieldsConfig).forEach((key) => {
        const field = fieldsConfig[key];
        if (!field) return;

        // Resolve placeholder
        if (
          field.placeholder &&
          typeof field.placeholder === 'string' &&
          field.placeholder.startsWith('ek_')
        ) {
          const translation = t(field.placeholder);
          if (translation && translation.content) {
            field.placeholder = translation.content;
          }
        }

        // Resolve aria-label (injected by Braintree as the iframe title for NVDA)
        if (
          field['aria-label'] &&
          typeof field['aria-label'] === 'string' &&
          field['aria-label'].startsWith('nc_')
        ) {
          const translation = t(field['aria-label']);
          if (translation && translation.content) {
            field['aria-label'] = translation.content;
          }
        }
      });
    }

    const config = {
      client: braintreeClient,
      styles: customStyles || paymentConfig.braintree.hostedFields.styles,
      fields: fieldsConfig
    };

    const hostedFields = await braintree.hostedFields.create(config);

    // Cache the instance
    hostedFieldsInstance = hostedFields;

    // console.log('✅ Hosted Fields created successfully');
    return hostedFields;
  } catch (error) {
    // console.error('❌ Failed to create Hosted Fields:', error);
    throw new Error('Failed to initialize card form. Please refresh and try again.');
  }
}

/**
 * Tokenize card data from Hosted Fields
 * Call this when user submits the payment form
 *
 * @param {Object} hostedFields - Hosted Fields instance
 * @param {Object} [options] - Additional tokenization options
 * @returns {Promise<Object>} Full payload with nonce, bin, lastTwo, etc.
 */
export async function tokenizeHostedFields(hostedFields, options = {}) {
  try {
    // console.log('🔵 Tokenizing card data...');

    const payload = await hostedFields.tokenize(options);

    // console.log('✅ Card tokenized successfully');
    // console.log('[Tokenize Payload]', {
    //   nonce: payload.nonce,
    //   bin: payload.details?.bin,
    //   cardType: payload.details?.cardType,
    //   expirationMonth: payload.details?.expirationMonth,
    //   expirationYear: payload.details?.expirationYear
    // });

    // Return payload with BIN extracted from details if available
    return {
      nonce: payload.nonce,
      bin: payload.details?.bin,
      type: payload.type,
      details: payload.details
    };
  } catch (error) {
    // console.error('❌ Failed to tokenize card:', error);

    // Provide user-friendly error messages
    if (error.code === 'HOSTED_FIELDS_FIELDS_EMPTY') {
      throw new Error('Please fill in all card details');
    }
    if (error.code === 'HOSTED_FIELDS_FIELDS_INVALID') {
      throw new Error('Please check your card details and try again');
    }
    if (error.code === 'HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE') {
      throw new Error('This card has already been used. Please try again.');
    }
    if (error.code === 'HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED') {
      throw new Error('CVV verification failed. Please check your card details.');
    }

    throw new Error('Failed to process card. Please try again.');
  }
}

/**
 * Validate specific field
 * @param {Object} hostedFields - Hosted Fields instance
 * @param {string} fieldName - Field name (number, cvv, expirationDate)
 * @returns {boolean} True if field is valid
 */
export function validateField(hostedFields, fieldName) {
  const state = hostedFields.getState();
  const field = state.fields[fieldName];

  return field && field.isValid;
}

/**
 * Validate all fields
 * @param {Object} hostedFields - Hosted Fields instance
 * @returns {Object} Validation result with details
 */
export function validateAllFields(hostedFields) {
  const state = hostedFields.getState();
  const { fields } = state;

  const validation = {
    isValid: true,
    fields: {}
  };

  Object.keys(fields).forEach((fieldName) => {
    const field = fields[fieldName];
    validation.fields[fieldName] = {
      isValid: field.isValid,
      isEmpty: field.isEmpty,
      isPotentiallyValid: field.isPotentiallyValid
    };

    if (!field.isValid) {
      validation.isValid = false;
    }
  });

  return validation;
}

/**
 * Clear all Hosted Fields
 * @param {Object} hostedFields - Hosted Fields instance
 */
export function clearHostedFields(hostedFields) {
  if (hostedFields) {
    hostedFields.clear();
    // console.log('🔵 Hosted Fields cleared');
  }
}

/**
 * Teardown Hosted Fields instance
 * Call when unmounting component or clearing payment form
 * @param {Object} hostedFields - Hosted Fields instance
 */
export async function teardownHostedFields(hostedFields) {
  if (hostedFields) {
    try {
      await hostedFields.teardown();
      // console.log('🔵 Hosted Fields torn down');
      hostedFieldsInstance = null;
    } catch (error) {
      if (error && error.code === 'METHOD_CALLED_AFTER_TEARDOWN') {
        // console.debug('[teardownHostedFields] teardown called after teardown — ignoring');
      } else {
        // console.error('Error tearing down Hosted Fields:', error);
      }
    }
  }
}

/**
 * Get current Hosted Fields instance
 * @returns {Object|null} Current instance or null
 */
export function getHostedFieldsInstance() {
  return hostedFieldsInstance;
}

/**
 * Set up event listeners for Hosted Fields
 * @param {Object} hostedFields - Hosted Fields instance
 * @param {Object} callbacks - Event callbacks
 * @param {Function} [callbacks.onFocus] - Field focus callback
 * @param {Function} [callbacks.onBlur] - Field blur callback
 * @param {Function} [callbacks.onChange] - Field change callback
 * @param {Function} [callbacks.onValidityChange] - Validity change callback
 */
export function setupFieldListeners(hostedFields, callbacks = {}) {
  if (callbacks.onFocus) {
    hostedFields.on('focus', callbacks.onFocus);
  }

  if (callbacks.onBlur) {
    hostedFields.on('blur', callbacks.onBlur);
  }

  if (callbacks.onChange) {
    hostedFields.on('change', callbacks.onChange);
  }

  if (callbacks.onValidityChange) {
    hostedFields.on('validityChange', callbacks.onValidityChange);
  }
}

export default {
  createHostedFields,
  tokenizeHostedFields,
  validateField,
  validateAllFields,
  clearHostedFields,
  teardownHostedFields,
  getHostedFieldsInstance,
  setupFieldListeners
};
